/**
 * Lift Block tag contract (A5): the one TypeScript reader.
 *
 * A pre/post pair is identified by a single tag, `lift:pre:<measure>` and
 * `lift:post:<measure>`. One tag rather than two because the pairing is the
 * only thing the tag exists to express, and two tags can disagree with each
 * other while one cannot. Stimulus-generic: the same pre/post seam serves any
 * within-subject reveal design.
 *
 * The backend runtime, the builder chips, the report's pair resolver and
 * `reporting-coherence` all read tags through here. The Python reader is
 * `apps/python-api/canonical_analysis/lift_identity.py`; both are held to
 * `tests/fixtures/reporting/lift-tags.v1.json`.
 */

export const LIFT_PRE_TAG_PREFIX = "lift:pre:";
export const LIFT_POST_TAG_PREFIX = "lift:post:";

export interface LiftTagHalf {
  half: "pre" | "post";
  measure: string;
}

/**
 * `{ half, measure }` for one lift tag, or null. The measure is trimmed; a tag
 * with an empty measure is not a lift tag.
 */
export function readLiftTag(tag: unknown): LiftTagHalf | null {
  if (typeof tag !== "string") {
    return null;
  }
  if (tag.startsWith(LIFT_PRE_TAG_PREFIX)) {
    const measure = tag.slice(LIFT_PRE_TAG_PREFIX.length).trim();
    return measure ? { half: "pre", measure } : null;
  }
  if (tag.startsWith(LIFT_POST_TAG_PREFIX)) {
    const measure = tag.slice(LIFT_POST_TAG_PREFIX.length).trim();
    return measure ? { half: "post", measure } : null;
  }
  return null;
}

/**
 * The first lift tag on a question, in tag order, or null. If a question
 * somehow carries both a pre and a post tag, the earliest wins.
 */
export function readQuestionLiftHalf(tags: Array<unknown> | null | undefined): LiftTagHalf | null {
  if (!Array.isArray(tags)) {
    return null;
  }
  for (const tag of tags) {
    const parsed = readLiftTag(tag);
    if (parsed) {
      return parsed;
    }
  }
  return null;
}
